<template>
  <div>
    <form class="section__content form">
      <fieldset class="form__group">
        <article class="form__item form__item--show-border">
          <label class="form__item__label">
            <h1 class="form__item__name">This Account Name</h1>
            <small class="form__item__description">What should we call this account?</small>
          </label>

          <section class="form__item__input input">
            <input type="text" class="input__field" v-if="currentAccount" v-model="accountName" />
          </section>
        </article>

        <article class="form__item form__item--show-border" v-if="accountNameDirty">
          <a href="#" v-on:click.prevent="saveAccountName" class="section__footer__button button button--primary">Save</a>
          <a href="#" v-on:click.prevent="cancelAccountName" class="section__footer__button section__footer__button--right button button--secondary">Cancel</a>
        </article>
      </fieldset>
    </form>

    <form v-if="planIsVisible" class="section__content form">
      <fieldset class="form__group">
        <article class="form__group">
          <aside class="choose-plan">
            <header class="window__header">
              <h1 class="window__title centered">Choose a plan</h1>
              <h1 class="window__description window__description--active" v-if="isCouponApplied">You have a limited edition coupon active on your plan</h1>
              <div class="button button--secondary" @click="togglePlanTerm">{{ getViewPlanText }}</div>
            </header>

            <section class="window__content pricing">
              <ul class="pricing__list" v-if="planTerm == 'month'">
                <li class="pricing__item" v-for="plan in getMonthlyPlans">
                  <article class="plan" v-on:click="choosePlan(plan)" :class="{'plan--selected': billing.plan_id == plan.id}">
                    <header class="plan__header">
                      <h1 class="plan__type">{{ plan.name }}</h1>
                      <h2 class="plan__price">
                              <span class="plan__price__value">
                                <span class="plan__price__currency">$</span><span :class="{'plan__coupon--strike': plan.id == isCouponApplied}">{{ plan.price / 100 }}</span>
                                <span v-if="plan.id == isCouponApplied" class="plan__price__currency" :class="{'plan__coupon': plan.id == isCouponApplied}">$</span><span v-if="plan.id == isCouponApplied" :class="{'plan__coupon': plan.id == isCouponApplied}">{{ (plan.price / 100) - currentAccount.coupon_amount_discounted }}</span>
                              </span>
                        <span class="plan__price__frequency">/ month </span>
                      </h2>
                    </header>
                    <ul class="plan__properties plan__properties--expanded">
                      <li class="plan__property" v-for="property in plan.properties">
                        {{ property }}
                      </li>
                    </ul>
                  </article>
                </li>
              </ul>
              <ul class="pricing__list" v-if="planTerm == 'year'">
                <li class="pricing__item" v-for="plan in getYearlyPlans">
                  <article class="plan" v-on:click="choosePlan(plan)" :class="{'plan--selected': billing.plan_id == plan.id}">
                    <header class="plan__header">
                      <h1 class="plan__type">{{ plan.name }}</h1>
                      <h2 class="plan__price">
                              <span class="plan__price__value">
                                <span class="plan__price__currency">$</span><span :class="{'plan__coupon--strike': plan.id == isCouponApplied}">{{ plan.price / 100 }}</span>
                                <span v-if="plan.id == isCouponApplied" class="plan__price__currency" :class="{'plan__coupon': plan.id == isCouponApplied}">$</span><span v-if="plan.id == isCouponApplied" :class="{'plan__coupon': plan.id == isCouponApplied}">{{ (plan.price / 100) - currentAccount.coupon_amount_discounted }}</span>
                              </span>
                        <span class="plan__price__frequency">/ year</span>
                      </h2>
                    </header>

                    <ul class="plan__properties">
                      <li class="plan__property" v-for="property in plan.properties">
                        {{ property }}
                      </li>
                    </ul>
                  </article>
                </li>
              </ul>
            </section>
          </aside>
        </article>

        <article class="form__item form__item--show-border" v-if="requiresCard(billing.plan)">
          <label class="form__item__label">
            <h1 class="form__item__name">Payment information</h1>
            <small class="form__item__description">Manage your saved card</small>
          </label>

          <section class="form__item__input input card__management">
            <credit-card-form :bus="bus" style="min-width: 500px;margin-bottom: 1em;" v-model="billing.token" v-if="needsCard()"></credit-card-form>
            <a href="#" v-on:click.prevent="saveCardInfo" v-if="billing.newCard" class="button button--primary">Save</a><a href="#" v-on:click="cancelUpdateCard" v-if="billing.newCard" class="button button--secondary">Cancel</a>
            <div v-if="billing.id && !billing.newCard && billing.cardType" style="min-width: 450px;text-align:right;">
              <span style="margin-right:1em;display:inline-block;">{{billing.cardType}} ending in {{billing.last4}} and expiring on {{billing.exp}}</span>
              <a style="display:inline-block" href="#" v-on:click.prevent="updateCard" class="button button--secondary">Update</a>
            </div>
          </section>
        </article>

        <article v-if="this.billing.dirty" class="form__item form__item--show-border">
          <a href="#" v-on:click.prevent="saveBilling" class="section__footer__button button button--primary">Save</a>
          <!-- <a href="#" v-on:click="cancelSaveBilling" class="section__footer__button section__footer__button--right button button--secondary">Cancel</a> -->
        </article>
      </fieldset>
    </form>

    <form class="section__content form section__team" v-if="getAccount && getAccount.plan.team">
      <fieldset class="form__group">
        <article class="form__item form__item--show-border">
          <label class="form__item__label">
            <h1 class="form__item__name"><strong>Current Account:</strong> {{ getAccount.name }}</h1>
          </label>

          <label class="form__item__label" v-if="getAccount.plan">
            <h1 v-if="getAccount.plan.max_users > 11" class="form__item__name">{{ getAccount.plan.name }}: ${{ (getAccount.plan.price / 100) }} per user in account</h1>
          </label>
          <div v-if="checkUserCount" class="button button--primary" @click="promptAddUser">Add User</div>
        </article>

        <article v-if="getAccountUsers" class="form__item form__item--show-border" v-for="user in getAccountUsers">
          <label class="form__item__label">
            <h1 class="form__item__name">{{ user.user.name }}</h1>
            <p class="form__item__subname">{{ capitalize(user.permissions) }}</p>
          </label>
          <section class="form__item__input input" v-if="isUserAccountAdmin && user.user_id !== getCurrentUser">
            <span>Toggle admin</span>
          </section>
          <section class="form__item__input input form__switch" v-if="isUserAccountAdmin && user.user_id !== getCurrentUser">
            <boolean @input="toggleUserPermissions(user.id, user.permissions)" :defaultValue="user.permissions == 'admin'" :id="user.user.id"></boolean>
          </section>
          <section class="form__item__input input" v-if="isUserAccountAdmin && user.user_id !== getCurrentUser">
            <div class="button button--faded" @click="removeUserFromAccount(user.id)">Remove User</div>
          </section>
        </article>
      </fieldset>
      <hr>
      <fieldset class="form__group" v-if="isUserAccountAdmin">
        <article class="form__item">
          <label class="form__item__label">
            <h1 class="form__item__name">Selective Formats for Users</h1>
            <small class="form__item__description">Which formats will the users of your account be able to see? (If there are no formats selected all of our formats will display to your users by default.)</small>
          </label>

          <section class="admin__input form__item__input input">
            <input type="text" class="input__field" placeholder="Search..."  v-model="adSearch" />
            <div id="admin__category__ul">
              <div v-for="format in formats">
                <a @click="adSearch = format.name" class="admin__li" v-if="format.name.toUpperCase().indexOf(adSearch.toUpperCase()) > -1 && format.name !== adSearch" href="#">{{ format.name }}</a>
              </div>
            </div>
          </section>
          <section class="admin__input form__item__input">
            <div @click="addFormat" class="admin__button--primary button button--primary">Add Format</div>
          </section>
        </article>
        <article v-for="ad in selectedFormats" class="form__item">
          <label class="form__item__label">
            <h1 class="form__item__name">{{ ad.name }}</h1>
          </label>
          <section class="admin__input form__item__input input">
            <div @click="deleteFormat(ad.id)" class="admin__button button button--remove">Remove</div>
          </section>
        </article>
      </fieldset>
    </form>

    <!--<div class="section__content">
      <p>Want to cancel your account? <a href="#" v-on:click="cancelAccount">Click here</a></p>
    </div>-->
    <form class="section__content form">
      <fieldset class="form__group">
        <div class="row">
          <div class="button button--primary" @click="promptCreateAccount">Create A New Account</div>
          <div v-if="isUserAccountOwner && !hasOneAccount" class="button button--faded" @click="promptDeleteAccount">Delete Current Account</div>
          <div class="help__tip">
            <p>You can make multiple accounts for your user and then invite other users to the account.</p>
          </div>
        </div>
      </fieldset>
    </form>
  </div>
</template>

<script>
  import Vue from 'vue'
  import store from '../store.js'
  import Boolean from './configurator/Boolean'
  import CreditCardForm from './common/CreditCardForm.vue'
  import {mapState} from 'vuex'

  export default {
    name: 'ProfileAccount',
    components: {
      'credit-card-form': CreditCardForm,
      'boolean': Boolean
    },
    props: ['billing', 'planTerm', 'currentAccount', 'plan_id'],
    data () {
      return {
        accountName: '',
        accountNameDirty: false,
        bus: new Vue(),
        adSearch: ''
      }
    },
    mounted () {
      if (this.plan_id) {
        this.billing.plan_id = this.plan_id;
      }

      this.bus.$on('creditCardEntered', ()=>{
        this.billing.dirty = true;
      });

      this.bus.$on('cardValidated', (token) => {
        console.log('card validated', token);
        this.billing.token = token;
        store.dispatch('accounts/update', this.billing);

        this.billing.dirty = false;
        this.billing.newCard = false;
      });

      this.bus.$on('noCardValidation', ()=>{
        store.dispatch('accounts/update', this.billing);
        this.billing.dirty = false;
      });

      if (this.currentAccount) {
        this.accountName = this.currentAccount.name;

        //this.selected_formats = this.$store.getters['']
      }

    },
    computed: {
      ...mapState(['plans', 'planChosen']),
      ...mapState(['account', 'me']),
      selectedFormats () {
        if (this.$store.state.formats.selectedFormats) {
          return this.$store.state.formats.selectedFormats.filter(f => !f.custom)
        } else {
          return [];
        }
      },
      formats () {
        let formats = this.$store.getters['formats/unfiltered']
        formats.forEach((f, index) => {
          this.selectedFormats.forEach(sf => {
            if (sf.id == f.id || f.custom) {
              formats.splice(index, 1);
            }
          })
        });
        return formats
      },
      planIsVisible () {
        return this.currentAccount && this.currentAccount.plan.visible
      },
      getMonthlyPlans () {
        return this.plans.filter(plan => plan.term == 'month')
      },
      getYearlyPlans () {
        return this.plans.filter(plan => plan.term == 'year')
      },
      getAccount () {
        if (this.account.id) {
          this.currentAccount = this.account;
        }
        return this.currentAccount;
      },
      isCouponApplied () {
        let account = this.currentAccount;
        if (!account) return;
        if (account.coupon_id) {
          return account.plan_id;
        } else {
          return false
        }
      },
      getAccountUsers () {
        return this.account.account_users;
      },
      getAccounts () {
        return store.getters['accounts/owned'];
      },
      isUserAccountAdmin () {
        let rtnValue = false;
        if (this.account.account_users) {
          this.account.account_users.forEach(account_user => {
            if (account_user.user_id === this.me.id) {
              if (account_user.permissions == 'admin') {
                rtnValue = true;
              }
            }
          })
        }
        return rtnValue
      },
      hasOneAccount () {
        if (store.getters['accounts/owned']) {
          return store.getters['accounts/owned'].length == 1;
        }
      },
      isUserAccountOwner () {
        let rtnValue = false;
        if (this.account.account_users) {
          this.account.account_users.forEach(account_user => {
            if (account_user.user_id === this.me.id) {
              if (account_user.owner) {
                rtnValue = true;
              }
            }
          })
        }
        return rtnValue
      },
      checkUserCount () {
        if (this.currentAccount.id) {
          if (this.currentAccount.account_users.length && this.billing.plan) {
            if (this.currentAccount.account_users.length == 1 && this.billing.plan.max_users == 1) {
              return true
            }
            return this.currentAccount.account_users.length < this.billing.plan.max_users
          }
        }
      },
      getViewPlanText () {
        if (this.planTerm == 'month') {
          return 'View Yearly Plans'
        } else if (this.planTerm == 'year') {
          return 'View Monthly Plans'
        }
      },
      getCurrentUser () {
        if (this.me.id) {
          return this.me.id;
        } else {
          return false
        }
      },
      currentAccountName () {
        if (this.currentAccount) {
          return this.currentAccount.name;
        }
      }
    },
    methods: {
      addFormat () {
        let ad = this.adSearch;
        this.formats.forEach(format => {
          if (format.name === ad) {
            this.$store.dispatch('accounts/formats/addSelected', {format_id: format.id})
          }
        });
        this.adSearch = '';
        return null
      },
      deleteFormat (format_id) {
        this.$modal.show('warning', {
          messageText: 'Are you sure you want to remove this format from visibility? (Users on your account will not be able to see this format until you re-add it)',
          buttonText: 'Remove Format',
          callback: function (params) {
            this.$store.dispatch('accounts/formats/deleteSelected', {format_id});
          }.bind(this)
        })
      },
      saveAccountName () {
        let payload = {
          name: this.accountName,
          id: this.currentAccount.id
        }
        store.dispatch('accounts/update', payload)
        this.accountNameDirty = false;
      },
      cancelAccountName () {
        this.accountName = this.currentAccount.name;
        this.accountNameDirty = false;
      },
      needsCard() {
        return !this.billing.id || this.billing.newCard || !this.billing.cardType
      },
      togglePlanTerm () {
        if (this.planTerm == 'month') {
          this.planTerm = 'year';
        } else if (this.planTerm == 'year') {
          this.planTerm = 'month';
        }
      },
      removeUserFromAccount (id) {
        store.dispatch('accounts/users/remove', {id: id});
      },
      toggleUserPermissions (id, perms) {
        let passperms;
        if (perms == 'admin') {
          passperms = 'normal'
        } else if (perms == 'normal') {
          passperms = 'admin'
        }
        store.dispatch('accounts/users/togglePerms', {id: id, permissions: passperms});
      },
      promptCreateAccount () {
        this.$modal.show('userinput', {
          modalTitle: 'Create Account',
          messageText: 'Enter the name of the account you want to create!',
          fieldType: 'text',
          fieldPlaceholder: 'ParkAve Inc.',
          buttonText: 'Create',
          callback (value) {
            store.dispatch('accounts/create', value);
          }
        });
      },
      prettyPrice(plan) {
        let price = (plan.price / 100).toFixed(2);
        return `$${price} / ${plan.term}`;
      },
      requiresCard(plan) {
        if (plan) {
          return plan.price > 0
        }
      },
      cancelAccount(e){
        this.$modal.show('warning', {
          messageText: 'Are you sure you want to cancel your account?',
          buttonText: 'Yes',
          callback (params) {
            store.dispatch('cancelAccount');
          }
        })
      },
      promptDeleteAccount () {
        this.$modal.show('warning', {
          messageText: 'Are you sure you want to delete your account?',
          buttonText: 'Yes',
          callback (params) {
            store.dispatch('accounts/delete', this.account.id);
          }
        })
      },
      saveCardInfo(e) {
        e.preventDefault();
        this.bus.$emit('billingFormSaved');
      },
      saveBilling(e) {
        console.log('billing', this.billing)

        if (this.needsCard()) {
          this.saveCardInfo(e);
          return;
        }

        e.preventDefault();
        store.dispatch('accounts/update', this.billing).then(() => {
          FS.event('Signed Up', {
            plan_name_str: this.billing.plan.name,
            plan_id: this.billing.plan_id,
            coupon_code_str: null,
            amount_discounted: 0
          });

          if (this.isProduction()) {
            var image = document.createElement('img');
            var script = document.createElement('script');
            script.src = 'https://www.dwin1.com/19038.js';
            script.defer = 'defer';
            script.type = 'text/javascript';
            image.src = `https://www.shareasale.com/sale.cfm?tracking=${this.billing.id}p${this.billing.plan_id}&amount=${(this.billing.plan.price / 100).toFixed(2)}&merchantID=97774&transtype=sale`;
            image.width = '1';
            image.height = '1';

            document.body.appendChild(image);
            document.body.appendChild(script);
          }
        });
        this.billing.dirty = false;
      },
      cancelSaveBilling(e){
        e.preventDefault();

        this.billing.token = null;
        this.billing.dirty = false;

        this.bus.$emit('cardCleared');
      },
      updateCard(e){
        e.preventDefault();

        this.billing.newCard = true;
      },
      cancelUpdateCard(e){
        e.preventDefault();

        this.billing.newCard = false;
      },
      choosePlan (plan) {
        if (this.billing.plan_id != plan.id && !this.billing.dirty) {
          this.billing.dirty = true;
        }
        this.billing.plan_id = plan.id;
        this.billing.plan = plan;
        if (plan.id == this.planChosen.id) {
          this.billing.dirty = false;
        }
      },
      promptAddUser () {
        if (this.billing.plan.max_users == 1) {
          this.$modal.show('pricing')
          this.$snotify.info('Want to add users to your account? Upgrade your account to a Team Account!', {timeout: 5000})
          return
        }

        this.$modal.show('userinput', {
          modalTitle: 'Invite User',
          messageText: 'Enter the email of the user you want to invite: (Also note that your monthly or yearly rate is prorated on how many users you added to the account)',
          fieldType: 'email',
          fieldPlaceholder: 'example@parkave.com',
          buttonText: 'Invite',
          callback (value) {
            store.dispatch('inviteUser', value);
          }
        });
      }
    },
    watch: {
      accountName(newVal) {
        if (newVal != this.currentAccount.name) {
          this.accountNameDirty = true;
        } else {
          this.accountNameDirty = false;
        }
      },
      currentAccountName(newVal) {
        if (newVal) {
          this.accountName = this.currentAccountName;
        }
      }
    }
  }
</script>

<style scoped>
  .form__switch {
    padding-right: 50px;
  }

  .button--primary {
    color: white !important;
  }
</style>



// WEBPACK FOOTER //
// src/components/ProfileAccount.vue